import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { Calendar, DateObject } from "react-multi-date-picker";
import "react-multi-date-picker/styles/layouts/mobile.css";
import { BsGeoAlt, BsPlayFill, BsStopFill, BsCupHotFill, BsBoxArrowRight, BsBoxArrowInRight, BsHourglassSplit } from "react-icons/bs";
import Handler from "../../Helpers/Handler";
import { formatDate, formatDateTime, showZoneTime, textCapitalize } from "../../Helpers/Utils";
import { Skeleton } from "../../Components/Needed";
import ErrorState from "../../Components/List/ErrorState";
import { LeaveCancelModal, leaveTypeText } from "../../Components/LeaveCancel";

const BREAK_TYPES = [
    { value: "BREAK", label: "Short break" },
    { value: "LUNCH", label: "Lunch" },
    { value: "ONDUTY", label: "On duty (off site)" },
    { value: "PERSONAL", label: "Personal" },
];
const BUCKETS = {
    PRESENT: { label: "Present", glyph: "P" },
    ABSENT: { label: "Absent", glyph: "A" },
    LEAVE: { label: "Leave", glyph: "L" },
    HALF_DAY: { label: "Half day", glyph: "H" },
    PERMISSION: { label: "Permission", glyph: "Pm" },
    OTHER: { label: "Off", glyph: "·" },
};
const BUCKET_ORDER = ["PRESENT", "ABSENT", "LEAVE", "HALF_DAY", "PERMISSION"];
const bucketOf = (day) => {
    const attendanceStatus = day?.attendance?.status;
    const leaveType = day?.leave?.type;
    if (leaveType === "HALF_DAY" || attendanceStatus === "HALF_DAY") return "HALF_DAY";
    if (leaveType || attendanceStatus === "LEAVE") return "LEAVE";
    if (day?.permission) return "PERMISSION";
    if (attendanceStatus === "PRESENT") return "PRESENT";
    if (attendanceStatus === "HOLIDAY" || attendanceStatus === "WEEKOFF") return "OTHER";
    if (attendanceStatus === "ABSENT" || day?.status === "ABSENT") return "ABSENT";
    return "OTHER";
};
const STATUS_LABEL = {
    PRESENT: "Present",
    ABSENT: "Absent",
    HALF_DAY: "Half day",
    HOLIDAY: "Holiday",
    WEEKOFF: "Week off",
    LEAVE: "Leave",
    SICK_LEAVE: "Sick leave",
    CASUAL_LEAVE: "Casual leave",
    EMERGENCY_LEAVE: "Emergency leave",
    PERMISSION: "Permission",
};
const toIsoDay = (date) => {
    const d = new Date(date);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const minutesToHuman = (minutes) => {
    const total = Math.max(0, Number(minutes) || 0);
    const hours = Math.floor(total / 60);
    const mins = total % 60;
    if (!hours) return `${mins}m`;
    return `${hours}h ${mins}m`;
};
const currentPosition = () => new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error("This browser can't share your location, so attendance can't be marked here."));
    navigator.geolocation.getCurrentPosition(
        (position) => resolve({ lat: position.coords.latitude, lng: position.coords.longitude }),
        (error) => {
            if (error.code === error.PERMISSION_DENIED) {
                reject(new Error("Location is blocked for this site. Allow it in your browser's address bar, then try again."));
            } else if (error.code === error.POSITION_UNAVAILABLE) {
                reject(new Error("Your location couldn't be determined. Check that location services are on."));
            } else {
                reject(new Error("Getting your location timed out. Try again."));
            }
        },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
});

const OverviewTab = () => {
    const [summary, setSummary] = useState(null);
    // Approved leave today blocks check-in until it is cancelled; this opens the cancel-request dialog for it.
    const [cancelLeave, setCancelLeave] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState("");
    const [breakType, setBreakType] = useState(BREAK_TYPES[0].value);
    const [breakRemarks, setBreakRemarks] = useState("");
    const [monthAnchor, setMonthAnchor] = useState(() => new Date());
    const [selectedDay, setSelectedDay] = useState(() => toIsoDay(new Date()));
    const [statusFilter, setStatusFilter] = useState(null);
    const [now, setNow] = useState(() => Date.now());
    const tickRef = useRef(null);

    const load = useCallback(async (options = {}) => {
        setLoading(true);
        const response = await Handler({ method: "post", url: "/staff/attendance/summary", data: { date: selectedDay, month: toIsoDay(monthAnchor) }, });
        if (response?.success && response?.data?.success) {
            setSummary(response?.data?.data || null);
            setError(null);
        } else {
            setError(response?.data?.message || "Unable to load your attendance");
        }
        setLoading(false);
    }, [selectedDay, monthAnchor]);

    useEffect(() => { load(); }, [load]);
    const openCheckIn = summary?.today?.checkIn;
    const restingUntil = summary?.today?.restingUntil;
    // Ticks for an open shift (the elapsed timer) and for the rest window (the
    // countdown) - both need a live clock, and an idle tab needs neither.
    useEffect(() => {
        if (!openCheckIn && !restingUntil) {
            if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
            return undefined;
        }
        setNow(Date.now());
        tickRef.current = setInterval(() => setNow(Date.now()), 1000);
        return () => { if (tickRef.current) clearInterval(tickRef.current); };
    }, [openCheckIn, restingUntil]);

    // When the rest window runs out, pull the summary again so the check-in
    // button comes back on its own instead of after a manual refresh.
    useEffect(() => {
        if (!restingUntil) return undefined;
        const wait = new Date(restingUntil).getTime() - Date.now();
        if (wait <= 0) return undefined;
        const timer = setTimeout(() => load({ silent: true }), wait + 1000);
        return () => clearTimeout(timer);
    }, [restingUntil, load]);
    const elapsed = useMemo(() => {
        if (!openCheckIn) return null;
        const seconds = Math.max(0, Math.floor((now - new Date(openCheckIn).getTime()) / 1000));
        const h = String(Math.floor(seconds / 3600)).padStart(2, "0");
        const m = String(Math.floor((seconds % 3600) / 60)).padStart(2, "0");
        const s = String(seconds % 60).padStart(2, "0");
        return `${h}:${m}:${s}`;
    }, [openCheckIn, now]);
    // Counts down off the same 1s tick the elapsed timer uses, so it reaches
    // zero on its own rather than sitting stale until the next reload.
    const restWait = useMemo(() => {
        const until = summary?.today?.restingUntil;
        if (!until) return null;
        const seconds = Math.ceil((new Date(until).getTime() - now) / 1000);
        if (seconds <= 0) return null;
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        return h ? `${h}h ${m}m` : `${Math.max(1, m)}m`;
    }, [summary, now]);

    const act = async (key, request) => {
        setBusy(key);
        try {
            const { lat, lng } = await currentPosition();
            const response = await Handler({ ...request, data: { ...(request.data || {}), lat, lng } });
            if (response?.success && response?.data?.success) {
                toast.success(response?.data?.message || "Done");
                setBreakRemarks("");
                await load({ silent: true });
            } else {
                toast.error(textCapitalize(response?.data?.message || "That didn't work"));
            }
        } catch (locationError) {
            toast.error(locationError.message);
        }
        setBusy("");
    };
    const actions = summary?.actions || {};
    const today = summary?.today || {};
    const activeLog = today?.activeLog;
    const dayRow = summary?.attendance;
    const sessions = summary?.sessions || (dayRow ? [dayRow] : []);
    const days = useMemo(() => (summary?.calendar || []).map((day) => ({ ...day, iso: toIsoDay(day.date), bucket: bucketOf(day), })), [summary]);
    const dayByIso = useMemo(() => Object.fromEntries(days.map((day) => [day.iso, day])), [days]);
    const counts = useMemo(() => {
        const totals = { PRESENT: 0, ABSENT: 0, LEAVE: 0, HALF_DAY: 0, PERMISSION: 0, worked: 0 };
        days.forEach((day) => {
            if (totals[day.bucket] !== undefined) totals[day.bucket] += 1;
            totals.worked += day.workedMinutes ?? (day.attendance?.totalWorkingMinutes || 0);
        });
        return totals;
    }, [days]);
    const filteredDays = useMemo(() => (statusFilter ? days.filter((day) => day.bucket === statusFilter).slice().reverse() : []), [days, statusFilter]);
    const mapDays = useCallback(({ date }) => {
        const iso = toIsoDay(date.toDate());
        const day = dayByIso[iso];
        if (!day) return {};
        const meta = BUCKETS[day.bucket];
        const dimmed = statusFilter && day.bucket !== statusFilter;
        const alsoPermission = Boolean(day.permission) && day.bucket !== "PERMISSION";
        return {
            className: `att-day att-${day.bucket.toLowerCase()}${dimmed ? " att-dim" : ""} att-${meta?.glyph} `,
            title: [
                `${formatDate(day.date)} — ${meta?.label || day.status}`,
                day.attendance?.checkIn && day.bucket !== "PRESENT" ? "worked part of the day" : "",
                day.sessionsCount > 1 ? `${day.sessionsCount} shifts` : "",
                day.leave?.type === "HALF_DAY" ? leaveTypeText(day.leave) : "",
                alsoPermission ? "permission approved" : "",
            ].filter(Boolean).join(" · "),
            children: (
                <span className="att-day-inner">
                    <span className="att-day-num">{date.format("D")}</span>
                    <span className="att-day-glyph" aria-hidden="true">{meta?.glyph}</span>
                    {alsoPermission ? <span className="att-day-perm" /> : null}
                    {/* {day.spansMidnight ? <span className="att-day-flag">+1d</span> : null} */}
                </span>
            ),
        };
    }, [dayByIso, statusFilter]);
    const calendarValue = useMemo(() => new DateObject(selectedDay), [selectedDay]);
    const calendarStart = useMemo(() => new DateObject(monthAnchor), [monthAnchor]);
    const maxSelectable = useMemo(() => new Date(), []);
    if (loading && !summary) {
        return (
            <div className="row g-3">
                <div className="col-12 col-lg-5"><Skeleton height={260} /></div>
                <div className="col-12 col-lg-7"><Skeleton height={260} /></div>
            </div>
        );
    }
    if (error && !summary) return <ErrorState message={error} onRetry={() => load()} />;
    return (
        <div className="row g-3">
            <div className="col-12 col-lg-5">
                <div className="card att-clock-card">
                    <div className="card-body">
                        {today?.carriedOver ? (
                            <div className="att-night-note">
                                Shift in progress — started {formatDate(today.shiftStartedOn)}
                            </div>
                        ) : null}

                        <div className="att-clock-label">{openCheckIn ? "On shift since" : "Not checked in"}</div>
                        <div className="att-clock-time">{openCheckIn ? showZoneTime(openCheckIn) : "--:--"}</div>
                        {elapsed ? <div className="att-clock-elapsed" aria-live="off">{elapsed}</div> : null}
                        {today?.rosterShift ? <div className="att-clock-shift">Rostered: {today.rosterShift}</div> : null}
                        {!openCheckIn && today?.shiftsToday > 0 && today.shiftsToday < today.maxShiftsPerDay ? (
                            <div className="att-clock-shift">
                                You've logged {today.shiftsToday} shift{today.shiftsToday > 1 ? "s" : ""} today. Checking in again starts shift {today.shiftsToday + 1}.
                            </div>
                        ) : null}
                        {/* The rest gap is enforced on the server; saying so here is
                            what stops it reading as a broken check-in button. */}
                        {restWait ? (
                            <div className="att-rest-note">
                                <BsHourglassSplit />
                                <span>
                                    {today.minRestHours || 1} hour gap needed between shifts — you can check in at{" "}
                                    <strong>{showZoneTime(today.restingUntil)}</strong> ({restWait} to go)
                                </span>
                            </div>
                        ) : null}
                        {activeLog ? (
                            <div className="att-break-active">
                                <BsCupHotFill />
                                <span>{textCapitalize(activeLog.type)} since {showZoneTime(activeLog.checkOut)}</span>
                            </div>
                        ) : null}
                        <div className="att-actions">
                            {actions.canCheckIn ? (
                                <button type="button" className="btn btn-primary att-action-btn" disabled={Boolean(busy)} onClick={() => act("in", { method: "post", url: "/staff/attendance/in" })}>
                                    <BsBoxArrowInRight /> {busy === "in" ? "Checking in..." : "Check in"}
                                </button>
                            ) : null}
                            {actions.canCheckOut ? (
                                <button type="button" className="btn btn-outline-danger att-action-btn" disabled={Boolean(busy)} onClick={() => act("out", { method: "patch", url: "/staff/attendance/out" })}>
                                    <BsBoxArrowRight /> {busy === "out" ? "Checking out..." : "Check out"}
                                </button>
                            ) : null}
                            {actions.canEndBreak ? (
                                <button type="button" className="btn btn-success att-action-btn" disabled={Boolean(busy)} onClick={() => act("logEnd", { method: "patch", url: "/staff/attendance/log/end" })}>
                                    <BsStopFill /> {busy === "logEnd" ? "Ending..." : "End break"}
                                </button>
                            ) : null}
                        </div>
                        {actions.canStartBreak ? (
                            <div className="att-break-form">
                                <label className="form-label small fw-semibold" htmlFor="att-break-type">Start a break</label>
                                <div className="d-flex gap-2 flex-wrap">
                                    <select id="att-break-type" className="form-select form-select-sm att-break-select" value={breakType} onChange={(event) => setBreakType(event.target.value)}>
                                        {BREAK_TYPES.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}
                                    </select>
                                    <input type="text" className="form-control form-control-sm att-break-remarks" placeholder="Remarks" value={breakRemarks} maxLength={200} onChange={(event) => setBreakRemarks(event.target.value)} />
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-secondary"
                                        disabled={Boolean(busy)}
                                        onClick={() => {
                                            if (!breakRemarks?.trim()) return toast.error("Remarks is required")
                                            act("logStart", { method: "post", url: "/staff/attendance/log/start", data: { type: breakType, ...(breakRemarks.trim() ? { remarks: breakRemarks.trim() } : {}) }, })
                                        }}
                                    >
                                        <BsPlayFill /> {busy === "logStart" ? "Starting..." : "Start"}
                                    </button>
                                </div>
                            </div>
                        ) : null}
                        {!actions.canCheckIn && !actions.canCheckOut && !actions.canEndBreak ? (
                            <div className="att-hint">
                                {today.leaveToday
                                    ? "You're on approved leave today, so you can't check in."
                                    : restWait
                                        ? "Resting between shifts."
                                        : "Nothing to do right now — today's shift is already closed."}
                                {today.leaveToday ? (
                                    today.leaveToday.cancelRequest?.status === "PENDING" ? (
                                        <div className="mt-2 small">Your cancel request is waiting for your supervisor. You can check in once it is approved.</div>
                                    ) : (
                                        <div className="mt-2">
                                            <div className="small mb-2">Plans changed? Ask your supervisor to cancel this leave, then you can check in.</div>
                                            <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => setCancelLeave(today.leaveToday)}>Request to cancel leave</button>
                                        </div>
                                    )
                                ) : null}
                            </div>
                        ) : null}
                        <div className="att-geo-note"><BsGeoAlt /> Your location is checked against your hub when you mark attendance.</div>
                    </div>
                </div>
            </div>
            <div className="col-12 col-lg-7">
                <div className="card att-calendar-card">
                    <div className="card-body">
                        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                            <div>
                                <h6 className="fw-semibold mb-0">Attendance calendar</h6>
                                <div className="text-muted small">Tap a tile to see only those days.</div>
                            </div>
                            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => { setMonthAnchor(new Date()); setSelectedDay(toIsoDay(new Date())); setStatusFilter(null); }}>
                                Today
                            </button>
                        </div>
                        <div className="att-tiles" role="group" aria-label="Filter the month by status">
                            {BUCKET_ORDER.map((key) => (
                                <button key={key} type="button" aria-pressed={statusFilter === key} className={`att-tile att-${key.toLowerCase()} ${statusFilter === key ? "active" : ""}`}
                                // onClick={() => setStatusFilter((prev) => (prev === key ? null : key))}
                                >
                                    <span className="att-tile-count">{counts[key]}</span>
                                    <span className="att-tile-label">{BUCKETS[key].label}</span>
                                </button>
                            ))}
                            {/* <div className="att-tile att-tile-static">
                                <span className="att-tile-count">{minutesToHuman(counts.worked)}</span>
                                <span className="att-tile-label">Worked</span>
                            </div> */}
                        </div>
                        {statusFilter ? (
                            <div className="att-filter-bar">
                                <span>
                                    Showing <strong>{filteredDays.length}</strong> {BUCKETS[statusFilter].label.toLowerCase()}
                                    {filteredDays.length === 1 ? " day" : " days"} this month
                                </span>
                                <button type="button" className="btn btn-sm btn-link p-0" onClick={() => setStatusFilter(null)}>Show all</button>
                            </div>
                        ) : null}
                        <div className={`att-calendar-wrap ${statusFilter ? "filtering" : ""}`}>
                            <Calendar
                                className="att-calendar"
                                value={calendarValue}
                                currentDate={calendarStart}
                                onChange={(date) => { if (date) setSelectedDay(toIsoDay(date.toDate())); }}
                                onMonthChange={(date) => setMonthAnchor(date.toDate())}
                                onYearChange={(date) => setMonthAnchor(date.toDate())}
                                mapDays={mapDays}
                                maxDate={maxSelectable}
                                format="YYYY-MM-DD"
                                shadow={false}
                            />
                        </div>
                        <div className="att-legend">
                            {BUCKET_ORDER.map((key) => (
                                <span key={key} className="att-legend-item">
                                    <span className={`att-legend-dot att-${key.toLowerCase()}`}>{BUCKETS[key].glyph}</span>
                                    {BUCKETS[key].label}
                                </span>
                            ))}
                        </div>
                        <hr className="my-3" />
                        {statusFilter ? (
                            <div className="att-day-list">
                                {filteredDays.length ? filteredDays.map((day) => (
                                    <button key={day.iso} type="button" className="att-list-row" onClick={() => { setSelectedDay(day.iso); setStatusFilter(null); }}>
                                        <span className={`att-list-pill att-${day.bucket.toLowerCase()}`}>{BUCKETS[day.bucket].glyph}</span>
                                        <span className="att-list-date">{formatDate(day.date)}</span>
                                        <span className="att-list-times">
                                            {day.attendance?.checkIn ? showZoneTime(day.attendance.checkIn) : "--:--"}
                                            {" → "}
                                            {day.attendance?.checkOut ? showZoneTime(day.attendance.checkOut) : (day.attendance?.checkIn ? "open" : "--:--")}
                                            {day.spansMidnight ? <span className="att-nextday">+1d</span> : null}
                                        </span>
                                        <span className="att-list-worked">
                                            {day.attendance ? minutesToHuman(day.workedMinutes ?? day.attendance.totalWorkingMinutes) : STATUS_LABEL[day.status] || ""}
                                            {day.sessionsCount > 1 ? <span className="att-chip att-muted att-list-sessions">{day.sessionsCount} shifts</span> : null}
                                        </span>
                                    </button>
                                )) : (
                                    <div className="text-muted small text-center py-3"> No {BUCKETS[statusFilter].label.toLowerCase()} days this month. </div>
                                )}
                            </div>
                        ) : (
                            <div className="att-day-detail">
                                <div className="fw-semibold mb-2">{formatDate(selectedDay)}</div>
                                {
                                    loading ? <>Loading...</> :
                                        sessions.length ? (
                                            <>
                                                {sessions.map((session, index) => (
                                                    <div key={session.attendanceId || index} className="att-session-block">
                                                        {sessions.length > 1 ? (
                                                            <div className="att-session-label">
                                                                Shift {index + 1}{session.shift ? ` · ${textCapitalize(session.shift)}` : ""}
                                                            </div>
                                                        ) : null}
                                                        <div className="att-day-row">
                                                            <span>In</span>
                                                            <strong>{session.checkIn ? formatDateTime(session.checkIn) : "-"}</strong>
                                                        </div>
                                                        <div className="att-day-row">
                                                            <span>Out</span>
                                                            <strong>
                                                                {session.checkOut ? formatDateTime(session.checkOut) : "Still open"}
                                                                {/* {index === sessions.length - 1 && dayByIso[selectedDay]?.spansMidnight ? <span className="att-nextday">+1d</span> : null} */}
                                                            </strong>
                                                        </div>
                                                        <div className="att-day-row"><span>Worked</span><strong>{minutesToHuman(session.totalWorkingMinutes)}</strong></div>
                                                        <div className="att-day-row"><span>Breaks</span><strong>{minutesToHuman(session.totalBreakMinutes)}</strong></div>
                                                        {session.logs?.length ? (
                                                            <div className="att-log-list">
                                                                {session.logs.map((log) => (
                                                                    <div key={log.logId} className="att-log-row">
                                                                        <span className="att-chip att-muted">{textCapitalize(log.type)}</span>
                                                                        <span>{showZoneTime(log.checkOut)} → {log.checkIn ? showZoneTime(log.checkIn) : "running"}</span>
                                                                        {log.remarks ? <em className="text-muted">{log.remarks}</em> : null}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ) : null}
                                                        {index < sessions.length - 1 ? <hr className="att-session-divider" /> : null}
                                                    </div>
                                                ))}
                                            </>
                                        ) : (
                                            <div className="text-muted small">
                                                {summary?.leave
                                                    ? `On ${leaveTypeText(summary.leave)} this day.`
                                                    : "No attendance recorded for this day."}
                                            </div>
                                        )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {cancelLeave ? (
                <LeaveCancelModal
                    leave={cancelLeave}
                    mode="request"
                    url={`/staff/leave/${cancelLeave.leaveId}/cancel`}
                    onClose={() => setCancelLeave(null)}
                    onDone={() => { setCancelLeave(null); load(); }}
                />
            ) : null}
        </div>
    );
};

export default OverviewTab;
